import { Injectable } from '@angular/core';

// Raptor
import { RaptorStepDataService } from './raptor-step-data.service';

@Injectable()
export class RaptorPriceService {

  constructor(
    private raptorStepDataService: RaptorStepDataService
  ) { }

  getPayableFirstInvoice(monthlyPremium: {[key: string]: any}, roundUp?: boolean) {
    if (monthlyPremium && monthlyPremium.payable_first_invoice) {
      return roundUp ? Math.ceil(parseFloat(monthlyPremium.payable_first_invoice)) : monthlyPremium.payable_first_invoice;
    } else {
      return undefined;
    }
  }

  getMonthlyTotalPayable(monthlyPremium: {[key: string]: any}): number {
    const { payable_monthly, payable_first_invoice } = monthlyPremium;
    let totalPayable = 0

    const payableMonthly = payable_monthly ? Math.ceil(parseFloat(payable_monthly)) : 0;
    const payableFirstInvoice = payable_first_invoice ? Math.ceil(parseFloat(payable_first_invoice)) : 0;

    if (payableMonthly > 0 && payableFirstInvoice > 0) {
      totalPayable = (payableMonthly * 11) + payableFirstInvoice;
    }

    return totalPayable;
  }

  getMonthlyPaymentBreakdown(monthlyPremium: {[key: string]: any}, currency: string): any {
    const { payable_first_invoice, payable_monthly } = monthlyPremium;
    let breakdown = null;

    if (payable_first_invoice > 0 && payable_monthly > 0) {
      breakdown = {
      firstMonthlyPayment: {
        amount: payable_first_invoice,
        currency: currency
      },
      subsequentPayment: {
        amount: payable_monthly,
        currency: currency
      }
    }};

    return breakdown;
  }

  getTotalPrice(selectedCover: any, frequency: string, settings?: any) {
    let price: any = {};
    let premium = selectedCover.premium[frequency];

    let premiumAttr = 'payable';

    if (frequency === 'U') {
      price.frequency = 'year';
      premiumAttr = 'payable';
    }
    if (frequency === 'M') {
      price.frequency = 'month';
      premiumAttr = 'payable_monthly';
    }
    if (settings.premium_attribute === 'payable') {
      price.frequency = 'year';
      premiumAttr = 'payable';
    }
    price.amount = premium && premium[premiumAttr];

    // remove frequency label if it is null
    if (premium && premium.frequency_label === null) {
      price.frequency = null;
    }

    if (
      settings &&
      settings.precision === 'round_up' &&
      typeof price.amount === 'number'
    ) {
      price.amount = Math.ceil(price.amount);
    }

    return price;
  }

  getPriceBreakdown(selectedCover: any, frequency: string, currency?: any) {
    let premium = selectedCover.premium[frequency];
    let priceBreakdown: Array<any> = [];
    let breakdown = premium?.breakdown;

    if (premium && Array.isArray(breakdown)) {
      breakdown.forEach((item: any, index: number) => {
        if (Array.isArray(item)) {
          item.forEach((price: any, i: number) => {
            priceBreakdown.push({
              currency: currency,
              amount: price.value,
              label: price.label,
              title: price.title || null,
              group_start: i === 0
            });
          });
        }
      });
    }
    return priceBreakdown;
  }

    /**
   * @ngdoc method
   * @name getCover
   * @methodOf RaptorPriceService
   * @description Retrieves a given cover from the latest version of formData
   * @returns {any} The provided cover
   */
  getCover(cover: any): any {
    if (!cover || typeof cover === 'undefined') {
      return null;
    }

    const formData = this.raptorStepDataService.get();

    return formData.covers.find((item: any) => 
      item.cover.product_slug === cover.cover.product_slug &&
      item.cover.cover_type === cover.cover.cover_type &&
      item.cover.cover_level === cover.cover.cover_level
    );
  }

  /**
   * @ngdoc method
   * @name getCoverPrice
   * @methodOf RaptorPriceService
   * @description Calculates the cover price
   * @returns {any} The discount
   */
  getCoverPrice(cover: any, frequency: 'M' | 'U', currencySymbol?: string): any {
    const updatedCover = this.getCover(cover);
    const formData = this.raptorStepDataService.get();
    const layoutSettings = formData?.settings;

    if (!currencySymbol) {
      currencySymbol = '$';
    }

    const price = {
      amount: 0,
      frequency: 'year',
      currency: currencySymbol
    };
    let premiumAttr = 'payable';

    if (!frequency) {
      frequency = 'M';
    }

    if (frequency === 'U') {
      price.frequency = 'year';
      premiumAttr = 'payable';
    }
    if (frequency === 'M') {
      price.frequency = 'month';
      premiumAttr = 'payable_monthly';
    }
    if (layoutSettings.premium_attribute === 'payable') {
      price.frequency = 'year';
      premiumAttr = 'payable';
    }

    if (updatedCover && updatedCover.premium) {
      price.amount = updatedCover.premium[frequency] && updatedCover.premium[frequency][premiumAttr];
      if (layoutSettings?.precision === 'round_up') {
        price.amount = Math.ceil(price.amount);
      }
    }

    return price;
  }

  /**
   * @ngdoc method
   * @name getCoverDiscount
   * @methodOf RaptorPriceService
   * @description Calculates the discount that has been applied to a cover.
   * @returns {any} The discount
   */
  getCoverDiscount(cover: any, frequency: 'M' | 'U', currency?: string, currencySymbol?: string): any {
    const updatedCover = this.getCover(cover);
    if (!currency) {
      currency = 'AUD';
    }
    if (!currencySymbol) {
      currencySymbol = '$';
    }

    let discount = {};
    let premiumAttr = 'payable';

    if (frequency === 'U') {
      premiumAttr = 'payable';
    }
    if (frequency === 'M') {
      premiumAttr = 'payable_monthly';
    }

    if (
      updatedCover &&
      updatedCover.premium &&
      updatedCover.premium[frequency] &&
      updatedCover.premium[frequency].prediscount_payable
    ) {
      discount = {
        amount: updatedCover.premium[frequency].prediscount_payable,
        currency: currency
      };

      if (updatedCover.premium[frequency][premiumAttr]) {
        discount = {
          ...discount,
          saving: `${currencySymbol}${Math.floor(updatedCover.premium[frequency].prediscount_payable - updatedCover.premium[frequency][premiumAttr])} saved`
        }
      }
    }
    return discount;
  }

  /**
   * @ngdoc method
   * @name getFirstMonthCoverPrice
   * @methodOf RaptorPriceService
   * @description Calculates the first month price for a given cover.
   * @returns {any} The first month price
   */
  getFirstMonthCoverPrice(cover: any): any {
    const updatedCover = this.getCover(cover);

    const price = {
      amount: updatedCover.premium.M.payable_first_invoice
    };
    
    return price;
  }

  /**
   * @ngdoc method
   * @name getYearlyCoverPrice
   * @methodOf RaptorPriceService
   * @description Calculates the yearly price for a given cover.
   * @returns {any} The yearly price + frequency
   */
  getYearlyCoverPrice(cover: any, currencySymbol?: string): any {
    const updatedCover = this.getCover(cover);
    const monthlyArrangFeeExtra = updatedCover.premium.M?.extras?.arrang_fee ?? {};
    
    if (!currencySymbol) {
      currencySymbol = '$';
    }

    const price = {
      amount: 0,
      frequency: 'year',
      currency: currencySymbol
    };

    // provide rounded up amount if PAF is enabled
    if (updatedCover.has_additional_charges && monthlyArrangFeeExtra.charge_upfront) {
      price.amount = this.getMonthlyTotalPayable(updatedCover.premium.M);
    } else {
      price.amount = Math.ceil(updatedCover.premium.M.payable);
    }
    return price;
  }

  /**
   * @ngdoc method
   * @name getArrangFee
   * @methodOf RaptorPriceService
   * @description Calculates the arrangement fee that has been applied to a cover.
   * @returns {any} The discount
   */
  getArrangFee(cover: any): any {
    const updatedCover = this.getCover(cover);

    const arrangFee = {
      payable: 0,
      is_included: false
    };
    if (updatedCover.premium.M?.extras?.arrang_fee) {
      arrangFee.payable = updatedCover.premium.M?.extras?.arrang_fee.payable;
      arrangFee.is_included = updatedCover.premium.M?.extras?.arrang_fee.is_included;
    }
    return arrangFee;
  }
}
